import React, { useEffect } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useHistory, useLocation } from "react-router-dom";
import { LoadingSpinner } from "../Components/LoadingSpinner";

type LoginLocationState = {
  returnTo?: string;
};

const resolveReturnTo = (value?: string) => {
  if (!value || value === "/") {
    return "/dashboard";
  }
  return value;
};

/** Auth0 login page with automatic post-authentication redirect. */
export default function Login(): React.ReactElement {
  const { isAuthenticated, isLoading, loginWithRedirect, error } = useAuth0();
  const history = useHistory();
  const location = useLocation<LoginLocationState>();

  const returnTo = resolveReturnTo(location.state?.returnTo);

  // While Auth0 is processing its redirect (/?code=&state=), the post-login landing belongs to
  // onRedirectCallback (Auth/redirectCallback.ts), which restores appState.returnTo through the
  // router history. Redirecting here too would clobber the preserved deep link with /dashboard
  // (location.state does not survive the Auth0 round-trip).
  const search = new URLSearchParams(location.search);
  const isAuth0Callback = search.has("code") && search.has("state");

  useEffect(() => {
    if (isAuthenticated && !isAuth0Callback) {
      history.replace(returnTo);
    }
  }, [isAuthenticated, isAuth0Callback, history, returnTo]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
      <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-8 shadow-xl">
        <div className="text-xs uppercase tracking-[0.3em] text-slate-500">
          BCC Admin
        </div>
        <h1 className="mt-2 text-2xl font-semibold text-slate-800">
          Admin login
        </h1>
        <p className="mt-3 text-sm text-slate-500">
          Sign in with your Auth0 account to access the admin console.
        </p>
        {error ? (
          <div className="mt-4 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
            {error.message}
          </div>
        ) : null}
        <button
          type="button"
          className="mt-6 w-full rounded-xl bg-slate-800 px-4 py-2.5 text-sm font-semibold text-white shadow hover:bg-slate-700"
          onClick={() =>
            loginWithRedirect({
              appState: { returnTo },
              authorizationParams: { prompt: "login" },
            })
          }
        >
          Log in
        </button>
      </div>
    </div>
  );
}
